import { useEffect } from "react";
import { ApolloError, gql, QueryHookOptions, useQuery } from "@apollo/client";
import { ShipmentStopType } from "@api/graphql/generated/generated-types";
import { useCourieStore } from "@src/common/lib/store";
import { showErrorToast } from "@src/common/lib/NetworkErrorHandling";

const SHIPMENT_MANUAL_ARRIVAL_OVERRIDE_QUERY = gql`
  query ShipmentManualArrivalOverride($courierId: ID!, $shipmentId: ID!) {
    shipmentById(courierId: $courierId, shipmentId: $shipmentId) {
      id
      driverId
      driver {
        id
      }
      stops {
        id
        stopSequenceNumber
        completedAt
        city
        companyName
        dispatchZone
        streetAddress
        suite
        state
        zipCode
        type
        tasks {
          id
          sequenceNumber
          status
          arrivedAt
          date
        }
      }
    }
  }
`;

export interface ShipmentManualArrivalOverrideQueryVariables {
  courierId: string;
  shipmentId: string;
}

export interface ShipmentManualArrivalOverrideQueryData {
  shipmentById: {
    id: string;
    driverId: string | null;
    driver: { id: string } | null;
    stops: Array<{
      id: string;
      type: ShipmentStopType;
      stopSequenceNumber: number | null;
      completedAt: number | null;
      tasks: Array<{
        id: string;
        sequenceNumber: number;
        arrivedAt: number | null;
        date: string;
      }> | null;
    }> | null;
  } | null;
}

export function useShipmentManualArrivalOverrideQuery(
  options: Pick<
    QueryHookOptions<
      ShipmentManualArrivalOverrideQueryData,
      ShipmentManualArrivalOverrideQueryVariables
    >,
    "variables" | "skip" | "fetchPolicy"
  >,
) {
  const queryResult = useQuery<
    ShipmentManualArrivalOverrideQueryData,
    ShipmentManualArrivalOverrideQueryVariables
  >(SHIPMENT_MANUAL_ARRIVAL_OVERRIDE_QUERY, options);

  const { showToast } = useCourieStore();

  useEffect(() => {
    if (queryResult.error)
      showErrorToast(queryResult.error as ApolloError, showToast);
  }, [queryResult.error, showToast]);

  return queryResult;
}
